/**
 * cwear-related-products.js
 * Handles Shopify Product Recommendations API + Quick Add
 * Patterns: IIFE scoping, section.id isolation, no page reload, IntersectionObserver lazy init
 */

(function () {
  'use strict';

  // ─── Constants ────────────────────────────────────────────────────────────

  const SELECTOR_SECTION = '[data-section-id]';
  const SELECTOR_GRID = '[data-cwear-rp-grid]';
  const SELECTOR_QUICK_ADD = '[data-cwear-quick-add]';
  const CLASS_SKELETON = 'cwear-rp__card--skeleton';
  const CLASS_CARD = 'cwear-rp__card';
  const CLASS_CARD_LINK = 'cwear-rp__card-link';
  const CLASS_IMG_WRAP = 'cwear-rp__img-wrap';
  const CLASS_QUICK_ADD_WRAP = 'cwear-rp__quick-add';
  const CLASS_QUICK_ADD_BTN = 'cwear-rp__quick-add-btn';
  const CLASS_INFO = 'cwear-rp__info';
  const CLASS_TITLE = 'cwear-rp__title';
  const CLASS_TITLE_SUB = 'cwear-rp__title-sub';
  const CLASS_PRICE = 'cwear-rp__price';
  const CLASS_PRICE_SALE = 'cwear-rp__price-sale';
  const CLASS_PRICE_COMPARE = 'cwear-rp__price-compare';
  const CLASS_SWATCHES = 'cwear-rp__swatches';
  const CLASS_SWATCH = 'cwear-rp__swatch';
  const CLASS_SWATCH_ACTIVE = 'is-active';
  const CLASS_SELECTORS = 'cwear-rp__selectors';
  const CLASS_SIZE_SELECT_WRAP = 'cwear-rp__size-wrap';
  const CLASS_SIZE_SELECT = 'cwear-rp__size-select';
  const CLASS_ERROR = 'cwear-rp__error';

  // ─── Entry point: find all cwear-related-products sections on page ────────

  const sections = document.querySelectorAll('.cwear-related-products');
  if (!sections.length) return;

  sections.forEach(function (section) {
    initSection(section);
  });

  // ─── Init per section instance ────────────────────────────────────────────

  function initSection(section) {
    const sectionId = section.dataset.sectionId;
    const productId = section.dataset.productId;
    const limit = parseInt(section.dataset.limit, 10) || 4;
    const grid = section.querySelector(SELECTOR_GRID);

    if (!sectionId || !grid) return;

    // If no product ID, we're not on a PDP — skip API, keep fallback blocks
    if (!productId || productId === '') {
      initQuickAdd(section, sectionId);
      return;
    }

    // Lazy init: only fetch when section enters viewport
    let initialized = false;

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !initialized) {
          initialized = true;
          observer.disconnect();
          fetchRecommendations(section, grid, productId, limit, sectionId);
        }
      });
    }, { threshold: 0.1 });

    observer.observe(section);
  }

  // ─── Fetch Shopify Product Recommendations ────────────────────────────────

  function fetchRecommendations(section, grid, productId, limit, sectionId) {
    var url = '/recommendations/products.json'
      + '?product_id=' + encodeURIComponent(productId)
      + '&limit=' + limit
      + '&intent=related';

    // Show skeleton placeholders while loading
    showSkeletons(grid, limit);

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Recommendations fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var products = data.products || [];

        if (!products.length) {
          // No recommendations — clear skeletons and keep fallback blocks
          clearSkeletons(grid);
          return;
        }

        renderProducts(grid, products, section, sectionId);
        initQuickAdd(section, sectionId);
      })
      .catch(function (err) {
        console.warn('[cwear-related-products] Recommendations error:', err);
        clearSkeletons(grid);
      });
  }

  // ─── Skeleton placeholders ────────────────────────────────────────────────

  function showSkeletons(grid, count) {
    // Remove existing fallback blocks temporarily
    grid.querySelectorAll('.' + CLASS_CARD).forEach(function (el) {
      el.style.display = 'none';
    });

    for (var i = 0; i < count; i++) {
      var skeleton = document.createElement('article');
      skeleton.className = CLASS_CARD + ' ' + CLASS_SKELETON;
      skeleton.setAttribute('aria-hidden', 'true');
      skeleton.innerHTML =
        '<div class="' + CLASS_IMG_WRAP + '"></div>' +
        '<div class="' + CLASS_INFO + '">' +
          '<p class="' + CLASS_TITLE + '">&nbsp;</p>' +
          '<p class="' + CLASS_PRICE + '">&nbsp;</p>' +
        '</div>';
      grid.appendChild(skeleton);
    }
  }

  function clearSkeletons(grid) {
    grid.querySelectorAll('.' + CLASS_SKELETON).forEach(function (el) {
      el.remove();
    });
    // Restore hidden fallback blocks
    grid.querySelectorAll('.' + CLASS_CARD).forEach(function (el) {
      el.style.display = '';
    });
  }

  // ─── Render API products into grid ───────────────────────────────────────

  function renderProducts(grid, products, section, sectionId) {
    // Remove skeletons and any hidden fallback blocks
    grid.innerHTML = '';

    var showSwatches = section.dataset.showSwatches !== 'false';
    var showQuickAdd = section.dataset.showQuickAdd !== 'false';
    var quickAddLabel = section.dataset.quickAddLabel || 'Agregar';

    products.forEach(function (product) {
      var card = buildCard(product, quickAddLabel, showQuickAdd, sectionId);
      grid.appendChild(card);
    });
  }

  // ─── Color name → hex lookup (mismo mapa que cwear-color-map.liquid,
  //     inyectado como JSON por la sección para no duplicar la lista) ────────

  var colorMapCache = {};

  function getColorMap(sectionId) {
    if (colorMapCache[sectionId]) return colorMapCache[sectionId];
    var el = document.getElementById('cwear-rp-color-map-' + sectionId);
    var map = {};
    if (el) {
      try {
        map = JSON.parse(el.textContent);
      } catch (e) {
        map = {};
      }
    }
    colorMapCache[sectionId] = map;
    return map;
  }

  function getColorHex(sectionId, colorName) {
    var map = getColorMap(sectionId);
    var key = String(colorName || '').toLowerCase().replace(/[\s\-_]/g, '');
    return map[key] || 'cccccc';
  }

  // ─── Build a single product card DOM ─────────────────────────────────────

  function buildCard(product, quickAddLabel, showQuickAdd, sectionId) {
    var article = document.createElement('article');
    article.className = CLASS_CARD;
    article.setAttribute('role', 'listitem');

    var link = document.createElement('a');
    link.className = CLASS_CARD_LINK;
    link.href = product.url;
    link.setAttribute('aria-label', product.title);

    // Image wrapper
    var imgWrap = document.createElement('div');
    imgWrap.className = CLASS_IMG_WRAP;
    var imgEl = null;

    if (product.featured_image) {
      imgEl = document.createElement('img');
      imgEl.className = 'cwear-rp__img';
      imgEl.alt = product.featured_image.alt || product.title;
      imgEl.loading = 'lazy';
      setImageSrc(imgEl, product.featured_image);
      imgWrap.appendChild(imgEl);
    }

    /* ── Selección de variante (talla/color) ──────────────────────────
       El Quick Add ya no agrega product.variants[0] a ciegas: cada
       opción con más de un valor posible (normalmente Talla y Color)
       necesita selección explícita del cliente antes de poder agregar
       — solo se auto-completa cuando NO hay ambigüedad real (una sola
       talla o un solo color disponible), a pedido explícito del
       usuario tras notar que se agregaba cualquier variante sin
       preguntar. */
    var selections = {};
    var optionMeta = buildOptionMeta(product); // [{name, position, values, isColor}]

    optionMeta.forEach(function (opt) {
      if (opt.values.length === 1) selections[opt.name] = opt.values[0];
    });

    var errorEl = document.createElement('p');
    errorEl.className = CLASS_ERROR;

    var qaBtn = null;

    function findVariant() {
      return (product.variants || []).find(function (v) {
        return optionMeta.every(function (opt) {
          return v['option' + opt.position] === selections[opt.name];
        });
      }) || null;
    }

    /* Imagen a mostrar al elegir un color — busca CUALQUIER variante con
       ese color que tenga foto propia, sin exigir que la talla ya esté
       elegida. Si se pidiera el match completo (findVariant), tocar el
       swatch antes de elegir talla nunca encontraba nada — la talla
       arranca sin seleccionar a propósito (no se asume por el usuario),
       así que el color por sí solo debe poder cambiar la imagen. */
    function findColorImage(colorOptPosition, colorValue) {
      var match = (product.variants || []).find(function (v) {
        return v['option' + colorOptPosition] === colorValue && v.featured_image;
      });
      return match ? match.featured_image : null;
    }

    // Quick add button (desktop hover only — aria-hidden, tabindex managed by CSS/JS)
    if (showQuickAdd && product.available) {
      var qaWrap = document.createElement('div');
      qaWrap.className = CLASS_QUICK_ADD_WRAP;
      qaWrap.setAttribute('aria-hidden', 'true');

      qaBtn = document.createElement('button');
      qaBtn.className = CLASS_QUICK_ADD_BTN;
      qaBtn.type = 'button';
      qaBtn.setAttribute('data-cwear-quick-add', '');
      qaBtn.textContent = quickAddLabel;

      qaWrap.appendChild(qaBtn);
      qaWrap.appendChild(errorEl);
      imgWrap.appendChild(qaWrap);

      qaBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var missing = optionMeta.filter(function (opt) { return !selections[opt.name]; });
        if (missing.length) {
          errorEl.textContent = 'Seleccioná ' + missing.map(function (o) { return o.name.toLowerCase(); }).join(' y ');
          return;
        }

        var variant = findVariant();
        if (!variant || !variant.available) {
          errorEl.textContent = 'Esa combinación no está disponible';
          return;
        }

        errorEl.textContent = '';
        addToCart(qaBtn, variant.id);
      });
    }

    link.appendChild(imgWrap);

    // Selectores de talla/color — SEPARADOS del <a> principal (no pueden
    // vivir dentro de un <a>: un <select> o <button> anidado en un link
    // rompe el foco/click nativo del navegador en ese control).
    var selectorsWrap = null;
    var colorOpt = optionMeta.find(function (o) { return o.isColor; });
    var otherOpts = optionMeta.filter(function (o) { return !o.isColor && o.values.length > 1; });

    if ((colorOpt && colorOpt.values.length > 1) || otherOpts.length) {
      selectorsWrap = document.createElement('div');
      selectorsWrap.className = CLASS_SELECTORS;
    }

    // Product info
    var info = document.createElement('div');
    info.className = CLASS_INFO;

    var titleSplit = splitTitle(product.title);
    var titleEl = document.createElement('p');
    titleEl.className = CLASS_TITLE;
    titleEl.textContent = titleSplit.main;
    info.appendChild(titleEl);

    if (titleSplit.sub) {
      var titleSubEl = document.createElement('p');
      titleSubEl.className = CLASS_TITLE_SUB;
      titleSubEl.textContent = titleSplit.sub;
      info.appendChild(titleSubEl);
    }

    var priceEl = document.createElement('p');
    priceEl.className = CLASS_PRICE;

    if (product.compare_at_price && product.compare_at_price > product.price) {
      var saleSpan = document.createElement('span');
      saleSpan.className = CLASS_PRICE_SALE;
      saleSpan.textContent = formatMoney(product.price);

      var compareSpan = document.createElement('span');
      compareSpan.className = CLASS_PRICE_COMPARE;
      compareSpan.textContent = formatMoney(product.compare_at_price);

      priceEl.appendChild(saleSpan);
      priceEl.appendChild(compareSpan);
    } else {
      priceEl.textContent = formatMoney(product.price);
    }

    info.appendChild(priceEl);

    link.appendChild(info);
    article.appendChild(link);

    // ── Swatches de color + selector de talla — FUERA del <a> a propósito:
    //    un <select> o <button> clickeable anidado dentro de un <a> rompe
    //    su foco/click nativo en varios navegadores. ──

    // Se llenan al construir cada <select> de talla más abajo; el click
    // de un swatch de color las vuelve a llamar para re-evaluar qué
    // tallas siguen disponibles con el nuevo color -- así el usuario
    // nunca puede elegir una combinación que no existe en stock.
    var refreshSizeAvailability = [];

    if (selectorsWrap) {
      if (colorOpt && colorOpt.values.length > 1) {
        var swatchesEl = document.createElement('div');
        swatchesEl.className = CLASS_SWATCHES;
        swatchesEl.setAttribute('aria-label', 'Colores disponibles');

        colorOpt.values.slice(0, 5).forEach(function (color, idx) {
          var swatch = document.createElement('button');
          swatch.type = 'button';
          swatch.className = CLASS_SWATCH;
          if (idx === 0) {
            swatch.classList.add(CLASS_SWATCH_ACTIVE);
            selections[colorOpt.name] = color;
          }
          swatch.title = color;
          swatch.setAttribute('aria-label', color);
          swatch.style.backgroundColor = '#' + getColorHex(sectionId, color);
          swatch.addEventListener('click', function () {
            selections[colorOpt.name] = color;
            errorEl.textContent = '';
            Array.prototype.forEach.call(swatchesEl.children, function (s) {
              s.classList.remove(CLASS_SWATCH_ACTIVE);
            });
            swatch.classList.add(CLASS_SWATCH_ACTIVE);

            // Bonus: si esta variante (con la talla ya elegida, si la hay)
            // tiene su propia foto, la imagen principal la refleja —
            // mismo criterio que el resto del sitio (PDP, Producto
            // Destacado) al cambiar de color.
            var colorImage = findColorImage(colorOpt.position, color);
            if (colorImage && imgEl) {
              setImageSrc(imgEl, colorImage.src || colorImage);
            }

            refreshSizeAvailability.forEach(function (fn) { fn(); });
          });
          swatchesEl.appendChild(swatch);
        });

        selectorsWrap.appendChild(swatchesEl);
      }

      otherOpts.forEach(function (opt) {
        var selectWrap = document.createElement('div');
        selectWrap.className = CLASS_SIZE_SELECT_WRAP;

        var select = document.createElement('select');
        select.className = CLASS_SIZE_SELECT;
        select.setAttribute('aria-label', opt.name);

        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = opt.name;
        select.appendChild(placeholder);

        opt.values.forEach(function (value) {
          var optionEl = document.createElement('option');
          optionEl.value = value;
          optionEl.textContent = value;
          select.appendChild(optionEl);
        });

        /* Deshabilita (sin ocultar) las tallas sin stock para el color
           elegido -- a pedido explícito del usuario, tras reportar que
           era frustrante elegir una talla y recién al intentar agregar
           enterarse de que no había. */
        function updateSizeAvailability() {
          var colorValue = colorOpt ? selections[colorOpt.name] : null;
          Array.prototype.forEach.call(select.options, function (optionEl) {
            if (!optionEl.value) return;
            var available = (product.variants || []).some(function (v) {
              if (v['option' + opt.position] !== optionEl.value) return false;
              if (colorOpt && colorValue && v['option' + colorOpt.position] !== colorValue) return false;
              return v.available;
            });
            optionEl.disabled = !available;
          });
        }
        updateSizeAvailability();
        refreshSizeAvailability.push(updateSizeAvailability);

        select.addEventListener('change', function () {
          selections[opt.name] = select.value || undefined;
          errorEl.textContent = '';
        });

        selectWrap.appendChild(select);
        selectorsWrap.appendChild(selectWrap);
      });

      article.appendChild(selectorsWrap);
    }

    return article;
  }

  // ─── Título largo: mismo criterio que cwear-pdp-main / cwear-cart-modal
  //     / cwear-cart — separador " — "/" – " si existe, si no, a la mitad
  //     por palabras (si tiene más de 4). ─────────────────────────────

  function splitTitle(title) {
    var emParts = title.split(' — ');
    if (emParts.length > 1) {
      return { main: emParts[0], sub: emParts.slice(1).join(' — ') };
    }
    var enParts = title.split(' – ');
    if (enParts.length > 1) {
      return { main: enParts[0], sub: enParts.slice(1).join(' – ') };
    }
    var words = title.split(' ');
    if (words.length > 4) {
      var half = Math.floor(words.length / 2);
      return { main: words.slice(0, half).join(' '), sub: words.slice(half).join(' ') };
    }
    return { main: title, sub: null };
  }

  // ─── Option metadata — nombre + posición + valores reales disponibles,
  //     y si es la opción de color (para swatches vs. <select> genérico) ──

  function buildOptionMeta(product) {
    return (product.options || []).map(function (opt, idx) {
      var name = (opt && opt.name) || ('Opción ' + (idx + 1));
      var lower = name.toLowerCase();
      var values = [];
      (product.variants || []).forEach(function (v) {
        var val = v['option' + (idx + 1)];
        if (val && values.indexOf(val) === -1) values.push(val);
      });
      return {
        name: name,
        position: idx + 1,
        values: values,
        isColor: lower === 'color' || lower === 'colour'
      };
    });
  }

  // ─── Helper: arma src + srcset a partir de una URL de imagen de Shopify ──

  function setImageSrc(img, srcOrObj) {
    var srcUrl = typeof srcOrObj === 'string' ? srcOrObj : (srcOrObj && srcOrObj.src);
    if (!srcUrl) return;
    var base = srcUrl.replace(/(\.[^.]+)(\?.*)?$/, '');
    var ext = (srcUrl.match(/\.[^.?]+/g) || ['.jpg']).pop();
    var query = (srcUrl.match(/\?.*$/) || [''])[0];
    img.src = base + '_600x' + ext + query;
    img.srcset = [
      base + '_375x' + ext + query + ' 375w',
      base + '_480x' + ext + query + ' 480w',
      base + '_600x' + ext + query + ' 600w',
      base + '_800x' + ext + query + ' 800w'
    ].join(', ');
    img.sizes = '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';
  }

  // ─── Quick Add to Cart ────────────────────────────────────────────────────

  function initQuickAdd(section, sectionId) {
    // Event delegation on the section root — avoids duplicate listeners
    section.addEventListener('click', function (e) {
      var btn = e.target.closest(SELECTOR_QUICK_ADD);
      if (!btn) return;

      // Prevent card link navigation
      e.preventDefault();
      e.stopPropagation();

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      addToCart(btn, variantId);
    });
  }

  function addToCart(btn, variantId) {
    if (btn.disabled) return;

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        btn.textContent = '✓';

        // Mismo patrón que cwear-pdp-main.js / cwear-featured-product.js:
        // Dawn's PUB_SUB_EVENTS.cartUpdate no tenía ningún listener real
        // conectado al bubble del header en este theme — el carrito SÍ se
        // agregaba (confirmado en /cart/add.js), pero la UI no se enteraba
        // hasta una navegación real a /cart, que es la que finalmente
        // renderizaba el estado real desde cero. Reportado por el usuario
        // tras probar el flujo completo.
        return updateCartBubble();
      })
      .then(function () {
        // Abre el cart modal — cwear-cart-modal.js escucha este evento
        try {
          document.dispatchEvent(new CustomEvent('cwear:cart:open'));
        } catch (e) {
          noop();
        }

        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1800);
      })
      .catch(function (err) {
        console.warn('[cwear-related-products] Cart error:', err);
        btn.textContent = originalText;
        btn.disabled = false;
      });
  }

  // ─── Refresh del bubble/contador del carrito vía Section Rendering API —
  //     mismo patrón que cwear-pdp-main.js, falla en silencio si no aplica ──

  function updateCartBubble() {
    if (typeof window.fetch !== 'function') return Promise.resolve();

    return window.fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections && sections['cart-icon-bubble']) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          var newBubble = doc.querySelector('#cart-icon-bubble');
          if (newBubble) bubble.innerHTML = newBubble.innerHTML;
        }
        return window.fetch('/cart.js');
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        document.querySelectorAll('[data-cwear-cart-count]').forEach(function (el) {
          el.textContent = count;
          el.style.display = count > 0 ? 'flex' : 'none';
        });
      })
      .catch(noop);
  }

  function noop() {}

  // ─── Money formatter ──────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    var amount = (cents / 100).toFixed(2);
    // Use Shopify's theme money format if available
    if (window.Shopify && window.Shopify.money_format) {
      return window.Shopify.money_format.replace('{{amount}}', amount)
        .replace('{{ amount }}', amount);
    }
    return '$' + amount;
  }

})();
